import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, useAuth } from '../contexts/AuthContext';

// Modulo de Backup gerenciavel: lista de jobs com seu cron + status, modal de
// criacao/edicao com browser SMB para escolher pasta de origem, browser de
// pastas do Drive para escolher destino, e historico de execucoes por job.

const RECORRENCIA_PRESETS = [
  { id: 'diario_00', label: 'Todos os dias as 00:00', cron: '0 0 * * *' },
  { id: 'diario_03', label: 'Todos os dias as 03:00', cron: '0 3 * * *' },
  { id: 'diario_22', label: 'Todos os dias as 22:00', cron: '0 22 * * *' },
  { id: 'seg_sex_22', label: 'Segunda a sexta as 22:00', cron: '0 22 * * 1-5' },
  { id: 'semanal_dom', label: 'Domingo as 02:00', cron: '0 2 * * 0' },
  { id: 'mensal_01', label: 'Dia 1 do mes as 02:00', cron: '0 2 1 * *' },
  { id: 'a_cada_hora', label: 'A cada hora (minuto 0)', cron: '0 * * * *' },
  { id: 'custom', label: 'Personalizado (cron)', cron: '' },
];

const STATUS_BADGE = {
  sucesso: 'bg-success',
  parcial: 'bg-warning text-dark',
  erro: 'bg-danger',
  rodando: 'bg-info text-dark',
  cancelada: 'bg-secondary',
};

function formatarData(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatarBytes(b) {
  if (b == null) return '—';
  const n = Number(b);
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1);
  return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 2)} ${u[i]}`;
}

function formatarDuracao(ms) {
  if (ms == null) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return `${m}m ${r}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function novoDestino(tipo = 'local') {
  return {
    tipo,
    // local
    local_caminho: '',
    // gdrive
    gdrive_folder_id: '',
    gdrive_folder_nome: '',
    // smb
    smb_host: '',
    smb_share: '',
    smb_caminho: '',
    smb_usuario: '',
    smb_senha: '',
    smb_dominio: '',
    // sftp/ftp (chamados "remoto")
    remoto_host: '',
    remoto_porta: '',
    remoto_caminho: '',
    remoto_usuario: '',
    remoto_senha: '',
    // storage interno (volume sshfs do portal)
    storage_subpasta: '',
  };
}

// Funcao em vez de constante para sempre retornar um objeto novo (com array
// `destinos` novo) — evita compartilhamento de referencia entre instancias
// do form quando o usuario abre "Novo job" varias vezes.
function formInicial() {
  return {
    id: null,
    nome: '',
    ativo: true,
    origem_host: '',
    origem_share: '',
    origem_caminho: '',
    origem_usuario: '',
    origem_senha: '',
    origem_dominio: '',
    origem_modo: 'pasta',
    origem_padrao: '',
    destinos: [novoDestino('local')],
    recorrencia: 'diario_00',
    cron_expressao: '0 0 * * *',
    max_backups: 4,
  };
}

function rotuloDestino(d) {
  if (d.tipo === 'local') return d.local_caminho || '(sem caminho)';
  if (d.tipo === 'gdrive') return d.gdrive_folder_nome || d.gdrive_folder_id || '(sem pasta)';
  if (d.tipo === 'smb') {
    const cam = d.smb_caminho ? `\\${d.smb_caminho}` : '';
    return `\\\\${d.smb_host}\\${d.smb_share || ''}${cam}`;
  }
  if (d.tipo === 'sftp' || d.tipo === 'ftp') {
    const porta = d.remoto_porta ? `:${d.remoto_porta}` : '';
    const cam = d.remoto_caminho ? `/${String(d.remoto_caminho).replace(/^\/+/, '')}` : '';
    return `${d.tipo.toUpperCase()} ${d.remoto_host}${porta}${cam}`;
  }
  if (d.tipo === 'storage_interno') {
    const sub = d.storage_subpasta ? `/${String(d.storage_subpasta).replace(/^\/+|\/+$/g, '')}` : '';
    return `Storage interno: ${sub || '/'}`;
  }
  return d.tipo;
}

function iconeDestino(tipo) {
  if (tipo === 'gdrive') return 'bi-google';
  if (tipo === 'smb') return 'bi-server';
  if (tipo === 'sftp') return 'bi-shield-lock';
  if (tipo === 'ftp') return 'bi-cloud-arrow-up';
  if (tipo === 'storage_interno') return 'bi-hdd-network';
  return 'bi-hdd';
}

const PORTAS_PADRAO_REMOTO = { sftp: 22, ftp: 21 };

export default function BackupManager() {
  const { temPermissao } = useAuth();
  const podeUsar = temPermissao('admin:backup');

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [driveStatus, setDriveStatus] = useState({ conectado: false });

  // Modal job
  const [form, setForm] = useState(formInicial);
  const [showModal, setShowModal] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [testandoSmb, setTestandoSmb] = useState(false);

  // Modal browser SMB
  const [showBrowser, setShowBrowser] = useState(false);
  const [browserAlvo, setBrowserAlvo] = useState('origem'); // 'origem' | 'destino'
  const [browserCaminho, setBrowserCaminho] = useState('');
  const [browserItems, setBrowserItems] = useState([]);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [browserErro, setBrowserErro] = useState('');
  // Ref espelha o ultimo browserCaminho — usado pelo handler "Selecionar esta
  // pasta" para evitar race com o setState quando o usuario clica logo apos
  // navegar (React pode nao ter renderizado a closure nova ainda).
  const browserCaminhoRef = useRef('');
  useEffect(() => { browserCaminhoRef.current = browserCaminho; }, [browserCaminho]);

  // Modal pastas Drive
  const [showDrivePastas, setShowDrivePastas] = useState(false);
  const [drivePastas, setDrivePastas] = useState([]);
  const [drivePastaPai, setDrivePastaPai] = useState('root');
  const [drivePastaBreadcrumb, setDrivePastaBreadcrumb] = useState([{ id: 'root', name: 'Meu Drive' }]);
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveBusca, setDriveBusca] = useState('');

  // Modal historico
  const [histJob, setHistJob] = useState(null);
  const [execucoes, setExecucoes] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  // Drive: indice do destino que esta selecionando pasta (multi-destino)
  const [driveDestinoIdx, setDriveDestinoIdx] = useState(null);

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      const [rJobs, rDrive] = await Promise.all([
        axios.get(`${API}/backup/jobs`),
        axios.get(`${API}/backup/drive/status`),
      ]);
      setJobs(rJobs.data.data || []);
      setDriveStatus(rDrive.data.data || { conectado: false });
      setErro('');
    } catch (e) {
      setErro(e?.response?.data?.error || 'Erro ao carregar jobs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // ---------- Drive OAuth ----------
  async function conectarDrive() {
    try {
      const { data } = await axios.get(`${API}/backup/drive/auth-url`);
      const w = window.open(data.data.url, 'gdrive_auth', 'width=520,height=640');
      if (!w) {
        Swal.fire({ icon: 'error', title: 'Bloqueado', text: 'Permita pop-ups para autorizar.' });
        return;
      }
      // Poll do status enquanto a janela esta aberta
      const timer = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/backup/drive/status`);
          if (r.data.data?.conectado) {
            setDriveStatus(r.data.data);
            clearInterval(timer);
            try { w.close(); } catch { /* */ }
          }
        } catch { /* */ }
      }, 2500);
      // Limpa o intervalo apos 5 min como guarda
      setTimeout(() => clearInterval(timer), 5 * 60 * 1000);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e?.response?.data?.error || e.message });
    }
  }

  async function desconectarDrive() {
    const conf = await Swal.fire({
      icon: 'question', title: 'Desconectar Drive?',
      text: 'Jobs com destino Drive nao conseguirao mais enviar backups.',
      showCancelButton: true, confirmButtonText: 'Sim',
    });
    if (!conf.isConfirmed) return;
    await axios.delete(`${API}/backup/drive/conexao`);
    setDriveStatus({ conectado: false });
  }

  // ---------- CRUD job ----------
  function abrirNovoJob() {
    setForm(formInicial());
    setShowModal(true);
  }

  function abrirEdicaoJob(job) {
    const preset = RECORRENCIA_PRESETS.find((p) => p.cron === job.cron_expressao);
    setForm({
      id: job.id,
      nome: job.nome || '',
      ativo: !!job.ativo,
      origem_host: job.origem_host || '',
      origem_share: job.origem_share || '',
      origem_caminho: job.origem_caminho || '',
      origem_usuario: job.origem_usuario || '',
      origem_senha: '',                  // nao volta do backend
      origem_dominio: job.origem_dominio || '',
      origem_modo: job.origem_modo || 'pasta',
      origem_padrao: job.origem_padrao || '',
      destinos: (job.destinos || []).map((d) => ({
        id: d.id,
        tipo: d.tipo,
        local_caminho: d.local_caminho || '',
        gdrive_folder_id: d.gdrive_folder_id || '',
        gdrive_folder_nome: d.gdrive_folder_nome || '',
        smb_host: d.smb_host || '',
        smb_share: d.smb_share || '',
        smb_caminho: d.smb_caminho || '',
        smb_usuario: d.smb_usuario || '',
        smb_senha: '',                  // nao volta do backend
        smb_dominio: d.smb_dominio || '',
        remoto_host: d.remoto_host || '',
        remoto_porta: d.remoto_porta || '',
        remoto_caminho: d.remoto_caminho || '',
        remoto_usuario: d.remoto_usuario || '',
        remoto_senha: '',               // nao volta do backend
        storage_subpasta: d.storage_subpasta || '',
      })),
      recorrencia: preset?.id || 'custom',
      cron_expressao: job.cron_expressao || '0 0 * * *',
      max_backups: Number(job.max_backups) || 4,
    });
    setShowModal(true);
  }

  // ---------- destinos: helpers ----------
  function adicionarDestino(tipo = 'local') {
    setForm((f) => ({ ...f, destinos: [...(f.destinos || []), novoDestino(tipo)] }));
  }
  function removerDestino(idx) {
    setForm((f) => ({ ...f, destinos: f.destinos.filter((_, i) => i !== idx) }));
  }
  function atualizarDestino(idx, patch) {
    setForm((f) => ({
      ...f,
      destinos: f.destinos.map((d, i) => (i === idx ? { ...d, ...patch } : d)),
    }));
  }
  function trocarTipoDestino(idx, novoTipo) {
    // Preserva id (se editando) mas zera campos especificos do tipo antigo
    setForm((f) => ({
      ...f,
      destinos: f.destinos.map((d, i) => {
        if (i !== idx) return d;
        return { ...novoDestino(novoTipo), id: d.id };
      }),
    }));
  }

  function alterarRecorrencia(id) {
    const preset = RECORRENCIA_PRESETS.find((p) => p.id === id);
    setForm((f) => ({
      ...f,
      recorrencia: id,
      cron_expressao: preset?.cron || f.cron_expressao,
    }));
  }

  async function salvarJob(e) {
    e.preventDefault();
    if (!form.nome || !form.origem_host || !form.origem_usuario) {
      Swal.fire({ icon: 'error', title: 'Campos obrigatorios', text: 'Preencha nome, host e usuario.' });
      return;
    }
    if (!form.id && !form.origem_senha) {
      Swal.fire({ icon: 'error', title: 'Senha obrigatoria', text: 'Senha SMB e obrigatoria ao criar um novo job.' });
      return;
    }
    if (!form.destinos || form.destinos.length === 0) {
      Swal.fire({ icon: 'error', title: 'Sem destinos', text: 'Adicione pelo menos um destino.' });
      return;
    }
    try {
      setSalvando(true);
      const payload = { ...form };
      if (!payload.origem_senha) delete payload.origem_senha;
      // Para cada destino: tira senha SMB vazia (preserva a gravada no backend)
      payload.destinos = (form.destinos || []).map((d) => {
        const copia = { ...d };
        if (!copia.smb_senha) delete copia.smb_senha;
        if (!copia.remoto_senha) delete copia.remoto_senha;
        return copia;
      });
      if (form.id) {
        await axios.put(`${API}/backup/jobs/${form.id}`, payload);
      } else {
        await axios.post(`${API}/backup/jobs`, payload);
      }
      setShowModal(false);
      await carregar();
      Swal.fire({ icon: 'success', title: 'Salvo!', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err?.response?.data?.error || err.message });
    } finally {
      setSalvando(false);
    }
  }

  async function excluirJob(job) {
    const conf = await Swal.fire({
      icon: 'warning', title: `Excluir "${job.nome}"?`,
      text: 'O historico tambem sera removido. Os backups ja gravados nao sao apagados.',
      showCancelButton: true, confirmButtonText: 'Excluir', confirmButtonColor: '#dc3545',
    });
    if (!conf.isConfirmed) return;
    await axios.delete(`${API}/backup/jobs/${job.id}`);
    await carregar();
  }

  async function executarJob(job) {
    const conf = await Swal.fire({
      icon: 'question', title: `Executar "${job.nome}" agora?`,
      showCancelButton: true, confirmButtonText: 'Executar',
    });
    if (!conf.isConfirmed) return;
    try {
      await axios.post(`${API}/backup/jobs/${job.id}/executar`);
      Swal.fire({ icon: 'success', title: 'Disparado!', text: 'Acompanhe pelo Historico.', timer: 1500, showConfirmButton: false });
      setTimeout(carregar, 2000);
    } catch (err) {
      // 409 = ja esta rodando — oferece parar a execucao atual
      if (err?.response?.status === 409) {
        const msg = err.response.data?.error || 'Job ja esta rodando.';
        const r = await Swal.fire({
          icon: 'warning',
          title: 'Ja em execucao',
          text: msg,
          showCancelButton: true,
          confirmButtonText: 'Parar execucao atual',
          confirmButtonColor: '#dc3545',
          cancelButtonText: 'Fechar',
        });
        if (r.isConfirmed) {
          try {
            await axios.post(`${API}/backup/jobs/${job.id}/parar`);
            Swal.fire({ icon: 'success', title: 'Pedido enviado',
              text: 'A execucao sera interrompida no proximo arquivo processado.',
              timer: 2500, showConfirmButton: false });
            setTimeout(carregar, 3000);
          } catch (e2) {
            Swal.fire({ icon: 'error', title: 'Erro ao parar',
              text: e2?.response?.data?.error || e2.message });
          }
        }
      } else {
        Swal.fire({ icon: 'error', title: 'Erro',
          text: err?.response?.data?.error || err.message });
      }
    }
  }

  // ---------- SMB browser ----------
  // browserAlvo pode ser: 'origem' OU 'destino:<idx>' (ex.: 'destino:2')
  function destinoDoAlvo(alvo) {
    if (!alvo || !alvo.startsWith('destino:')) return null;
    const idx = Number(alvo.split(':')[1]);
    return { idx, dest: form.destinos?.[idx] };
  }

  function checarConexaoSmb(alvo) {
    if (alvo?.startsWith('destino:')) {
      const { dest } = destinoDoAlvo(alvo);
      if (!dest?.smb_host || !dest?.smb_usuario || (!dest?.id && !dest?.smb_senha)) {
        Swal.fire({ icon: 'info', title: 'Preencha o destino SMB primeiro' });
        return false;
      }
    } else {
      if (!form.origem_host || !form.origem_usuario
          || (!form.id && !form.origem_senha)) {
        Swal.fire({ icon: 'info', title: 'Preencha a origem SMB primeiro' });
        return false;
      }
    }
    return true;
  }

  async function abrirBrowserSmb(alvo = 'origem') {
    if (!checarConexaoSmb(alvo)) return;
    let inicial = '';
    if (alvo?.startsWith('destino:')) {
      const { dest } = destinoDoAlvo(alvo);
      inicial = dest?.smb_caminho || '';
    } else {
      inicial = form.origem_caminho || '';
    }
    setBrowserAlvo(alvo);
    setBrowserCaminho(inicial);
    setShowBrowser(true);
    await carregarBrowser(inicial, alvo);
  }

  // Monta o payload SMB conforme o alvo (origem ou destino:<idx>).
  function payloadSmbDoAlvo(alvo, overrideForm = null) {
    const base = overrideForm || form;
    if (alvo?.startsWith('destino:')) {
      const idx = Number(alvo.split(':')[1]);
      const dest = base.destinos?.[idx] || {};
      return {
        smb_host: dest.smb_host,
        smb_share: dest.smb_share,
        smb_caminho: dest.smb_caminho,
        smb_usuario: dest.smb_usuario,
        smb_senha: dest.smb_senha,
        smb_dominio: dest.smb_dominio,
        destino_id: dest.id,
        job_id: base.id || undefined,
      };
    }
    return {
      origem_host: base.origem_host,
      origem_share: base.origem_share,
      origem_caminho: base.origem_caminho,
      origem_usuario: base.origem_usuario,
      origem_senha: base.origem_senha,
      origem_dominio: base.origem_dominio,
      job_id: base.id || undefined,
    };
  }

  async function carregarBrowser(caminho, alvo = browserAlvo, overrideForm = null) {
    setBrowserCaminho(caminho);
    browserCaminhoRef.current = caminho;
    setBrowserLoading(true);
    setBrowserErro('');
    try {
      const payload = {
        ...payloadSmbDoAlvo(alvo, overrideForm),
        caminho,
        alvo: alvo?.startsWith('destino:') ? 'destino' : 'origem',
      };
      const { data } = await axios.post(`${API}/backup/smb/navegar`, payload);
      const ordem = { share: 0, pasta: 1, arquivo: 2 };
      setBrowserItems((data.data || []).sort((a, b) => {
        const da = ordem[a.tipo] ?? 9;
        const db = ordem[b.tipo] ?? 9;
        if (da !== db) return da - db;
        return a.nome.localeCompare(b.nome);
      }));
    } catch (e) {
      setBrowserErro(e?.response?.data?.error || 'Falha ao navegar.');
      setBrowserItems([]);
    } finally {
      setBrowserLoading(false);
    }
  }

  async function clicarItemBrowser(it) {
    if (it.tipo === 'share') {
      let novoForm;
      if (browserAlvo?.startsWith('destino:')) {
        const idx = Number(browserAlvo.split(':')[1]);
        novoForm = {
          ...form,
          destinos: form.destinos.map((d, i) => (i === idx
            ? { ...d, smb_share: it.nome, smb_caminho: '' }
            : d)),
        };
      } else {
        novoForm = { ...form, origem_share: it.nome, origem_caminho: '' };
      }
      setForm(novoForm);
      await carregarBrowser('', browserAlvo, novoForm);
    } else if (it.tipo === 'pasta') {
      await carregarBrowser(it.caminho);
    }
  }

  function caminhoPai(c) {
    if (!c) return '';
    const partes = c.split(/[\\/]/).filter(Boolean);
    partes.pop();
    return partes.join('\\');
  }

  function selecionarCaminhoSmb() {
    const caminho = browserCaminhoRef.current;
    setForm((f) => {
      if (browserAlvo?.startsWith('destino:')) {
        const idx = Number(browserAlvo.split(':')[1]);
        return {
          ...f,
          destinos: f.destinos.map((d, i) => (i === idx ? { ...d, smb_caminho: caminho } : d)),
        };
      }
      return { ...f, origem_caminho: caminho };
    });
    setShowBrowser(false);
    Swal.fire({
      toast: true, position: 'top-end',
      icon: 'success',
      title: caminho ? `Pasta selecionada: ${caminho}` : 'Raiz do share selecionada',
      timer: 2000, showConfirmButton: false,
    });
  }

  async function testarSmb(alvo = 'origem') {
    if (!checarConexaoSmb(alvo)) return;
    setTestandoSmb(true);
    try {
      const payload = {
        ...payloadSmbDoAlvo(alvo),
        alvo: alvo?.startsWith('destino:') ? 'destino' : 'origem',
      };
      await axios.post(`${API}/backup/smb/testar`, payload);
      Swal.fire({ icon: 'success', title: 'Conexao OK', timer: 1500, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Falhou', text: e?.response?.data?.error || e.message });
    } finally {
      setTestandoSmb(false);
    }
  }

  // ---------- Drive folders ----------
  async function abrirBrowserDrive(destinoIdx) {
    if (!driveStatus.conectado) {
      Swal.fire({ icon: 'info', title: 'Conecte o Drive primeiro' });
      return;
    }
    setDriveDestinoIdx(destinoIdx);
    setDrivePastaPai('root');
    setDrivePastaBreadcrumb([{ id: 'root', name: 'Meu Drive' }]);
    setDriveBusca('');
    setShowDrivePastas(true);
    await carregarPastasDrive('root');
  }

  async function carregarPastasDrive(parent, busca = '') {
    setDriveLoading(true);
    try {
      const params = busca ? { busca } : { parent };
      const { data } = await axios.get(`${API}/backup/drive/pastas`, { params });
      setDrivePastas(data.data || []);
      setDrivePastaPai(parent);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e?.response?.data?.error || e.message });
    } finally {
      setDriveLoading(false);
    }
  }

  async function entrarPasta(p) {
    setDrivePastaBreadcrumb((bc) => [...bc, { id: p.id, name: p.name }]);
    await carregarPastasDrive(p.id);
  }

  async function voltarBreadcrumb(idx) {
    const novo = drivePastaBreadcrumb.slice(0, idx + 1);
    setDrivePastaBreadcrumb(novo);
    await carregarPastasDrive(novo[novo.length - 1].id);
  }

  function selecionarPastaAtual() {
    const atual = drivePastaBreadcrumb[drivePastaBreadcrumb.length - 1];
    setForm((f) => ({
      ...f,
      destinos: f.destinos.map((d, i) => (i === driveDestinoIdx
        ? {
          ...d,
          gdrive_folder_id: atual.id,
          gdrive_folder_nome: drivePastaBreadcrumb.map((b) => b.name).join(' / '),
        }
        : d)),
    }));
    setShowDrivePastas(false);
  }

  // ---------- Historico ----------
  async function abrirHistorico(job) {
    setHistJob(job);
    setHistLoading(true);
    try {
      const { data } = await axios.get(`${API}/backup/jobs/${job.id}/execucoes`, { params: { limit: 30 } });
      setExecucoes(data.data || []);
    } catch (e) {
      setExecucoes([]);
    } finally {
      setHistLoading(false);
    }
  }

  // ---------- Render ----------
  if (!podeUsar) {
    return (
      <div className="p-4">
        <div className="alert alert-warning">Voce nao tem permissao para acessar este modulo.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 text-center text-muted">
        <div className="spinner-border spinner-border-sm me-2" />
        Carregando jobs de backup...
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="d-flex align-items-start justify-content-between mb-4 gap-3">
        <div>
          <h4 className="mb-1">Backup Manager</h4>
          <p className="text-muted mb-0">
            Backups agendados de pastas compartilhadas (SMB) com destino local ou Google Drive.
          </p>
        </div>
        <button className="btn btn-primary" onClick={abrirNovoJob}>
          <i className="bi bi-plus-lg me-1" /> Novo job
        </button>
      </div>

      {erro && <div className="alert alert-danger">{erro}</div>}

      <div className="card mb-4">
        <div className="card-body d-flex align-items-center gap-3 flex-wrap">
          <div className="flex-grow-1">
            <strong className="d-block">Google Drive</strong>
            {driveStatus.conectado ? (
              <>
                <small className="text-muted d-block">
                  Conectado como <code>{driveStatus.conta_email || '—'}</code>
                  {' '} em {formatarData(driveStatus.conectado_em)}
                </small>
                {driveStatus.escopos?.length > 0 && (
                  <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>
                    Escopos: {driveStatus.escopos.map((s) => s.split('/').pop()).join(', ')}
                  </small>
                )}
                {driveStatus.pode_listar_pastas === false && (
                  <div className="alert alert-warning py-2 mt-2 mb-0" style={{ fontSize: '0.85rem' }}>
                    <i className="bi bi-exclamation-triangle me-1" />
                    O token atual NAO permite listar pastas (falta o escopo
                    <code className="mx-1">drive.metadata.readonly</code>).
                    Desconecte e conecte de novo para autorizar.
                  </div>
                )}
              </>
            ) : (
              <small className="text-muted">Nao conectado. Necessario para jobs com destino Drive.</small>
            )}
          </div>
          {driveStatus.conectado ? (
            <button className="btn btn-sm btn-outline-danger" onClick={desconectarDrive}>
              <i className="bi bi-box-arrow-right me-1" /> Desconectar
            </button>
          ) : (
            <button className="btn btn-sm btn-outline-primary" onClick={conectarDrive}>
              <i className="bi bi-google me-1" /> Conectar Drive
            </button>
          )}
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="card">
          <div className="card-body text-center text-muted py-5">
            <i className="bi bi-hdd-rack" style={{ fontSize: '2.5rem' }} />
            <p className="mt-2 mb-0">Nenhum job cadastrado. Clique em "Novo job" para comecar.</p>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Job</th>
                <th>Origem</th>
                <th>Destino</th>
                <th>Recorrencia</th>
                <th>Retencao</th>
                <th>Ultima execucao</th>
                <th style={{ minWidth: 180 }}>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id} className={job.ativo ? '' : 'opacity-50'}>
                  <td>
                    <strong>{job.nome}</strong>
                    <div>
                      {job.ativo
                        ? <span className="badge bg-success">Ativo</span>
                        : <span className="badge bg-secondary">Pausado</span>}
                    </div>
                  </td>
                  <td>
                    <code style={{ fontSize: '0.85rem' }}>
                      \\{job.origem_host}\{job.origem_share}
                      {job.origem_caminho ? `\\${job.origem_caminho}` : ''}
                    </code>
                  </td>
                  <td>
                    {(job.destinos || []).map((d, i) => (
                      <div key={d.id || i} className="mb-1">
                        <i className={`bi ${iconeDestino(d.tipo)} me-1`} />
                        <small>{rotuloDestino(d)}</small>
                      </div>
                    ))}
                    {(job.destinos || []).length === 0 && (
                      <small className="text-muted">(sem destinos)</small>
                    )}
                  </td>
                  <td>
                    <small>{job.cron_descricao || job.cron_descricao_calc || job.cron_expressao}</small>
                  </td>
                  <td>{job.max_backups}</td>
                  <td>
                    {job.ultima_execucao_em ? (
                      <>
                        <span className={`badge ${STATUS_BADGE[job.ultima_execucao_status] || 'bg-secondary'}`}>
                          {job.ultima_execucao_status || '—'}
                        </span>
                        <div><small className="text-muted">{formatarData(job.ultima_execucao_em)}</small></div>
                      </>
                    ) : <small className="text-muted">Nunca rodou</small>}
                  </td>
                  <td>
                    <div className="btn-group btn-group-sm">
                      <button className="btn btn-outline-primary" title="Executar agora" onClick={() => executarJob(job)}>
                        <i className="bi bi-play-fill" />
                      </button>
                      <button className="btn btn-outline-secondary" title="Historico" onClick={() => abrirHistorico(job)}>
                        <i className="bi bi-clock-history" />
                      </button>
                      <button className="btn btn-outline-secondary" title="Editar" onClick={() => abrirEdicaoJob(job)}>
                        <i className="bi bi-pencil" />
                      </button>
                      <button className="btn btn-outline-danger" title="Excluir" onClick={() => excluirJob(job)}>
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal job */}
      {showModal && (
        <ModalOverlay onClose={() => !salvando && setShowModal(false)}>
          <form className="modal-content" onSubmit={salvarJob} style={{ maxWidth: 760 }}>
            <div className="modal-header">
              <h5 className="modal-title">
                {form.id ? `Editar job #${form.id}` : 'Novo job de backup'}
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-8">
                  <label className="form-label">Nome do job *</label>
                  <input className="form-control" value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <div className="form-check form-switch">
                    <input id="ativo" type="checkbox" className="form-check-input"
                      checked={form.ativo}
                      onChange={(e) => setForm({ ...form, ativo: e.target.checked })} />
                    <label htmlFor="ativo" className="form-check-label">Ativo</label>
                  </div>
                </div>

                <div className="col-12"><hr className="my-1" /><h6 className="mb-0">Origem (SMB / CIFS)</h6></div>
                <div className="col-md-5">
                  <label className="form-label">Host / IP *</label>
                  <input className="form-control" placeholder="192.168.0.50" value={form.origem_host}
                    onChange={(e) => setForm({ ...form, origem_host: e.target.value })} required />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Share</label>
                  <input className="form-control" placeholder="backups" value={form.origem_share}
                    onChange={(e) => setForm({ ...form, origem_share: e.target.value })} />
                  <small className="text-muted">Em branco = raiz do host.</small>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Dominio</label>
                  <input className="form-control" placeholder="WORKGROUP" value={form.origem_dominio}
                    onChange={(e) => setForm({ ...form, origem_dominio: e.target.value })} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Usuario *</label>
                  <input className="form-control" value={form.origem_usuario}
                    onChange={(e) => setForm({ ...form, origem_usuario: e.target.value })} required />
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Senha {form.id ? <small className="text-muted">(em branco mantem)</small> : '*'}
                  </label>
                  <input className="form-control" type="password" value={form.origem_senha}
                    onChange={(e) => setForm({ ...form, origem_senha: e.target.value })}
                    autoComplete="new-password" />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <button type="button" className="btn btn-outline-secondary w-100"
                    onClick={() => testarSmb('origem')} disabled={testandoSmb}>
                    {testandoSmb ? 'Testando...' : 'Testar conexao'}
                  </button>
                </div>
                <div className="col-12">
                  <label className="form-label">Pasta (subcaminho dentro do share)</label>
                  <div className="input-group">
                    <input className="form-control" placeholder="Fotos\2026"
                      value={form.origem_caminho}
                      onChange={(e) => setForm({ ...form, origem_caminho: e.target.value })} />
                    <button type="button" className="btn btn-outline-secondary" onClick={() => abrirBrowserSmb('origem')}>
                      <i className="bi bi-folder2-open me-1" /> Navegar
                    </button>
                  </div>
                  <small className="text-muted">Em branco = raiz do share.</small>
                </div>

                <div className="col-md-5">
                  <label className="form-label">Modo da origem</label>
                  <select className="form-select" value={form.origem_modo || 'pasta'}
                    onChange={(e) => setForm({ ...form, origem_modo: e.target.value })}>
                    <option value="pasta">Pasta inteira (todos os arquivos e subpastas)</option>
                    <option value="arquivo_mais_recente">Apenas o arquivo mais recente da pasta</option>
                  </select>
                  <small className="text-muted">
                    "Mais recente" pega so o arquivo com o maior mtime dentro da pasta — util pra dump de banco cujo nome muda a cada rodada.
                  </small>
                </div>
                <div className="col-md-7">
                  <label className="form-label">
                    Padrao do nome <small className="text-muted">(opcional, usado no modo "mais recente")</small>
                  </label>
                  <input className="form-control" placeholder="dump_*.bak"
                    value={form.origem_padrao || ''}
                    disabled={form.origem_modo !== 'arquivo_mais_recente'}
                    onChange={(e) => setForm({ ...form, origem_padrao: e.target.value })} />
                  <small className="text-muted">Glob simples com <code>*</code> e <code>?</code>. Em branco = qualquer arquivo.</small>
                </div>

                <div className="col-12">
                  <hr className="my-1" />
                  <div className="d-flex align-items-center justify-content-between">
                    <h6 className="mb-0">Destinos <small className="text-muted">(pelo menos 1)</small></h6>
                    <button type="button" className="btn btn-sm btn-outline-primary"
                      onClick={() => adicionarDestino('local')}>
                      <i className="bi bi-plus-lg me-1" /> Adicionar destino
                    </button>
                  </div>
                </div>
                {(form.destinos || []).map((d, idx) => (
                  <div key={idx} className="col-12">
                    <div className="border rounded p-3 mb-2" style={{ background: 'rgba(128, 128, 128, 0.08)' }}>
                      <div className="row g-2 align-items-end">
                        <div className="col-md-4">
                          <label className="form-label small mb-1">Destino #{idx + 1} — Tipo *</label>
                          <select className="form-select form-select-sm" value={d.tipo}
                            onChange={(e) => trocarTipoDestino(idx, e.target.value)}>
                            <option value="local">Storage local (servidor)</option>
                            <option value="storage_interno">Storage interno (NAS do portal)</option>
                            <option value="smb">Compartilhamento SMB / CIFS</option>
                            <option value="sftp">Servidor SFTP (SSH)</option>
                            <option value="ftp">Servidor FTP</option>
                            <option value="gdrive">Google Drive</option>
                          </select>
                        </div>
                        <div className="col-md-7 text-muted small">
                          <i className={`bi ${iconeDestino(d.tipo)} me-1`} />
                          {rotuloDestino(d)}
                        </div>
                        <div className="col-md-1 text-end">
                          {form.destinos.length > 1 && (
                            <button type="button" className="btn btn-sm btn-outline-danger"
                              onClick={() => removerDestino(idx)} title="Remover este destino">
                              <i className="bi bi-trash" />
                            </button>
                          )}
                        </div>
                      </div>

                      {d.tipo === 'local' && (
                        <div className="row g-2 mt-1">
                          <div className="col-12">
                            <label className="form-label small">Caminho no servidor *</label>
                            <input className="form-control form-control-sm"
                              placeholder="/var/backups/portal-ti"
                              value={d.local_caminho}
                              onChange={(e) => atualizarDestino(idx, { local_caminho: e.target.value })} />
                            <small className="text-muted">Caminho dentro do container/maquina do backend. Sera criado se nao existir.</small>
                          </div>
                        </div>
                      )}

                      {d.tipo === 'storage_interno' && (
                        <div className="row g-2 mt-1">
                          <div className="col-12">
                            <label className="form-label small">Subpasta (opcional)</label>
                            <input className="form-control form-control-sm"
                              placeholder="diarios"
                              value={d.storage_subpasta}
                              onChange={(e) => atualizarDestino(idx, { storage_subpasta: e.target.value })} />
                            <small className="text-muted">
                              Grava em <code>&lt;BACKUP_STORAGE_ROOT&gt;{d.storage_subpasta ? `/${String(d.storage_subpasta).replace(/^\/+|\/+$/g, '')}` : ''}</code> (NAS via sshfs, default <code>/srv/backups</code> &lt;- <code>/pools/A/A0/backups</code>). Em branco = direto na raiz.
                            </small>
                          </div>
                        </div>
                      )}

                      {d.tipo === 'gdrive' && (
                        <div className="row g-2 mt-1">
                          <div className="col-12">
                            <label className="form-label small">Pasta no Google Drive *</label>
                            <div className="input-group input-group-sm">
                              <input className="form-control" readOnly
                                placeholder="Nenhuma pasta selecionada"
                                value={d.gdrive_folder_nome || d.gdrive_folder_id} />
                              <button type="button" className="btn btn-outline-secondary" onClick={() => abrirBrowserDrive(idx)}>
                                <i className="bi bi-folder2-open me-1" /> Escolher
                              </button>
                            </div>
                            {!driveStatus.conectado && (
                              <small className="text-warning">Conecte o Google Drive antes de salvar.</small>
                            )}
                          </div>
                        </div>
                      )}

                      {d.tipo === 'smb' && (
                        <div className="row g-2 mt-1">
                          <div className="col-md-5">
                            <label className="form-label small">Host / IP *</label>
                            <input className="form-control form-control-sm" placeholder="192.168.0.60"
                              value={d.smb_host}
                              onChange={(e) => atualizarDestino(idx, { smb_host: e.target.value })} />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small">Share</label>
                            <input className="form-control form-control-sm" placeholder="backups"
                              value={d.smb_share}
                              onChange={(e) => atualizarDestino(idx, { smb_share: e.target.value })} />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label small">Usuario *</label>
                            <input className="form-control form-control-sm"
                              value={d.smb_usuario}
                              onChange={(e) => atualizarDestino(idx, { smb_usuario: e.target.value })} />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label small">
                              Senha {d.id ? <small className="text-muted">(em branco mantem)</small> : '*'}
                            </label>
                            <input className="form-control form-control-sm" type="password"
                              value={d.smb_senha}
                              onChange={(e) => atualizarDestino(idx, { smb_senha: e.target.value })}
                              autoComplete="new-password" />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label small">Dominio</label>
                            <input className="form-control form-control-sm" placeholder="WORKGROUP"
                              value={d.smb_dominio}
                              onChange={(e) => atualizarDestino(idx, { smb_dominio: e.target.value })} />
                          </div>
                          <div className="col-md-4 d-flex align-items-end">
                            <button type="button" className="btn btn-sm btn-outline-secondary w-100"
                              onClick={() => testarSmb(`destino:${idx}`)} disabled={testandoSmb}>
                              {testandoSmb ? 'Testando...' : 'Testar destino'}
                            </button>
                          </div>
                          <div className="col-12">
                            <label className="form-label small">Subpasta no share</label>
                            <div className="input-group input-group-sm">
                              <input className="form-control" placeholder="Backups\PortalTI"
                                value={d.smb_caminho}
                                onChange={(e) => atualizarDestino(idx, { smb_caminho: e.target.value })} />
                              <button type="button" className="btn btn-outline-secondary"
                                onClick={() => abrirBrowserSmb(`destino:${idx}`)}>
                                <i className="bi bi-folder2-open me-1" /> Navegar
                              </button>
                            </div>
                            <small className="text-muted">Sera criada se nao existir. Em branco = raiz do share.</small>
                          </div>
                        </div>
                      )}

                      {(d.tipo === 'sftp' || d.tipo === 'ftp') && (
                        <div className="row g-2 mt-1">
                          <div className="col-md-7">
                            <label className="form-label small">Host / IP *</label>
                            <input className="form-control form-control-sm"
                              placeholder={d.tipo === 'sftp' ? 'sftp.exemplo.com' : 'ftp.exemplo.com'}
                              value={d.remoto_host}
                              onChange={(e) => atualizarDestino(idx, { remoto_host: e.target.value })} />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label small">Porta</label>
                            <input className="form-control form-control-sm" type="number" min="1" max="65535"
                              placeholder={String(PORTAS_PADRAO_REMOTO[d.tipo])}
                              value={d.remoto_porta}
                              onChange={(e) => atualizarDestino(idx, { remoto_porta: e.target.value })} />
                            <small className="text-muted">Padrao {PORTAS_PADRAO_REMOTO[d.tipo]}</small>
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small">Usuario *</label>
                            <input className="form-control form-control-sm"
                              value={d.remoto_usuario}
                              onChange={(e) => atualizarDestino(idx, { remoto_usuario: e.target.value })} />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small">
                              Senha {d.id ? <small className="text-muted">(em branco mantem)</small> : '*'}
                            </label>
                            <input className="form-control form-control-sm" type="password"
                              value={d.remoto_senha}
                              onChange={(e) => atualizarDestino(idx, { remoto_senha: e.target.value })}
                              autoComplete="new-password" />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small">Pasta remota</label>
                            <input className="form-control form-control-sm"
                              placeholder={d.tipo === 'sftp' ? '/home/user/backups' : 'backups'}
                              value={d.remoto_caminho}
                              onChange={(e) => atualizarDestino(idx, { remoto_caminho: e.target.value })} />
                            <small className="text-muted">Sera criada se nao existir. Em branco = pasta home do usuario.</small>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <div className="col-12"><hr className="my-1" /><h6 className="mb-0">Agendamento e retencao</h6></div>
                <div className="col-md-6">
                  <label className="form-label">Recorrencia *</label>
                  <select className="form-select" value={form.recorrencia}
                    onChange={(e) => alterarRecorrencia(e.target.value)}>
                    {RECORRENCIA_PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Expressao cron</label>
                  <input className="form-control" value={form.cron_expressao}
                    onChange={(e) => setForm({ ...form, cron_expressao: e.target.value, recorrencia: 'custom' })}
                    required />
                  <small className="text-muted">Formato: min hora dia mes diaSemana</small>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Manter ate *</label>
                  <input className="form-control" type="number" min="1" max="50" value={form.max_backups}
                    onChange={(e) => setForm({ ...form, max_backups: Number(e.target.value) })} required />
                  <small className="text-muted">backups</small>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={salvando}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* Modal browser SMB */}
      {showBrowser && (
        <ModalOverlay onClose={() => setShowBrowser(false)}>
          <div className="modal-content" style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <h5 className="modal-title">
                Navegar SMB — {browserAlvo?.startsWith('destino:')
                  ? `destino #${Number(browserAlvo.split(':')[1]) + 1}`
                  : 'origem'}
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowBrowser(false)} />
            </div>
            <div className="modal-body">
              {(() => {
                const ehDestino = browserAlvo?.startsWith('destino:');
                const idxDest = ehDestino ? Number(browserAlvo.split(':')[1]) : -1;
                const destAtual = ehDestino ? form.destinos?.[idxDest] : null;
                const host = ehDestino ? destAtual?.smb_host : form.origem_host;
                const share = ehDestino ? destAtual?.smb_share : form.origem_share;
                const listandoShares = !share;
                const subirNivel = () => {
                  if (browserCaminho) {
                    carregarBrowser(caminhoPai(browserCaminho));
                  } else if (share) {
                    // Volta para a lista de shares do host
                    const limpo = ehDestino
                      ? { ...form, destinos: form.destinos.map((d, i) => (i === idxDest ? { ...d, smb_share: '', smb_caminho: '' } : d)) }
                      : { ...form, origem_share: '', origem_caminho: '' };
                    setForm(limpo);
                    carregarBrowser('', browserAlvo, limpo);
                  }
                };
                return (
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <button className="btn btn-sm btn-outline-secondary"
                      disabled={!browserCaminho && !share}
                      onClick={subirNivel}>
                      <i className="bi bi-arrow-up" />
                    </button>
                    <code className="flex-grow-1 text-truncate">
                      \\{host}{share ? `\\${share}` : ''}{browserCaminho ? `\\${browserCaminho}` : ''}
                      {listandoShares && <span className="text-muted ms-2">(escolha um compartilhamento)</span>}
                    </code>
                  </div>
                );
              })()}
              {browserErro && <div className="alert alert-danger py-2">{browserErro}</div>}
              {browserLoading ? (
                <div className="text-center text-muted py-3">
                  <div className="spinner-border spinner-border-sm me-2" /> Carregando...
                </div>
              ) : (
                <div className="list-group" style={{ maxHeight: 360, overflowY: 'auto' }}>
                  {browserItems.length === 0 && <small className="text-muted">(vazio)</small>}
                  {browserItems.map((it, idx) => (
                    <button key={`${it.tipo}:${it.caminho || it.nome}:${idx}`} type="button"
                      className="list-group-item list-group-item-action d-flex align-items-center gap-2"
                      onClick={() => clicarItemBrowser(it)}
                      disabled={it.tipo === 'arquivo'}>
                      {it.tipo === 'share' && <i className="bi bi-hdd-network-fill text-primary" />}
                      {it.tipo === 'pasta' && <i className="bi bi-folder-fill text-warning" />}
                      {it.tipo === 'arquivo' && <i className="bi bi-file-earmark text-muted" />}
                      <span className="flex-grow-1">{it.nome}</span>
                      {it.tipo === 'share' && it.comentario && <small className="text-muted">{it.comentario}</small>}
                      {it.tipo === 'arquivo' && <small className="text-muted">{formatarBytes(it.tamanho)}</small>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowBrowser(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={selecionarCaminhoSmb}
                disabled={!(browserAlvo?.startsWith('destino:')
                  ? form.destinos?.[Number(browserAlvo.split(':')[1])]?.smb_share
                  : form.origem_share)}>
                Selecionar esta pasta
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Modal browser Drive */}
      {showDrivePastas && (
        <ModalOverlay onClose={() => setShowDrivePastas(false)}>
          <div className="modal-content" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h5 className="modal-title">Pasta do Google Drive</h5>
              <button type="button" className="btn-close" onClick={() => setShowDrivePastas(false)} />
            </div>
            <div className="modal-body">
              <div className="input-group input-group-sm mb-2">
                <span className="input-group-text"><i className="bi bi-search text-secondary" /></span>
                <input className="form-control" placeholder="Buscar pasta por nome em qualquer lugar do Drive..."
                  value={driveBusca}
                  onChange={(e) => setDriveBusca(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); carregarPastasDrive(drivePastaPai, driveBusca); } }} />
                {driveBusca && (
                  <button className="btn btn-outline-secondary" onClick={() => { setDriveBusca(''); carregarPastasDrive('root'); }}>
                    Limpar
                  </button>
                )}
                <button className="btn btn-outline-primary" onClick={() => carregarPastasDrive(drivePastaPai, driveBusca)}>
                  Buscar
                </button>
              </div>
              {!driveBusca && (
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    {drivePastaBreadcrumb.map((b, idx) => (
                      <li key={b.id} className={`breadcrumb-item ${idx === drivePastaBreadcrumb.length - 1 ? 'active' : ''}`}>
                        {idx === drivePastaBreadcrumb.length - 1 ? b.name : (
                          <button className="btn btn-link p-0" onClick={() => voltarBreadcrumb(idx)}>{b.name}</button>
                        )}
                      </li>
                    ))}
                  </ol>
                </nav>
              )}
              {driveLoading ? (
                <div className="text-center text-muted py-3">
                  <div className="spinner-border spinner-border-sm me-2" /> Carregando...
                </div>
              ) : (
                <div className="list-group" style={{ maxHeight: 340, overflowY: 'auto' }}>
                  {drivePastas.length === 0 && (
                    <div className="text-muted small p-2">
                      {driveBusca
                        ? '(nenhuma pasta encontrada para essa busca)'
                        : '(sem subpastas aqui)'}
                      {driveStatus.pode_listar_pastas === false && (
                        <div className="text-warning mt-1">
                          O token atual nao tem permissao para listar pastas. Desconecte e
                          reconecte o Drive para autorizar o escopo <code>drive.metadata.readonly</code>.
                        </div>
                      )}
                    </div>
                  )}
                  {drivePastas.map((p) => (
                    <button key={p.id} type="button"
                      className="list-group-item list-group-item-action d-flex align-items-center gap-2"
                      onClick={() => entrarPasta(p)}>
                      <i className="bi bi-folder-fill text-warning" />
                      <span>{p.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDrivePastas(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={selecionarPastaAtual}>
                Salvar nesta pasta
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Modal historico */}
      {histJob && (
        <ModalOverlay onClose={() => setHistJob(null)}>
          <div className="modal-content" style={{ maxWidth: 880 }}>
            <div className="modal-header">
              <h5 className="modal-title">Historico — {histJob.nome}</h5>
              <button type="button" className="btn-close" onClick={() => setHistJob(null)} />
            </div>
            <div className="modal-body">
              {histLoading ? (
                <div className="text-center text-muted py-3">
                  <div className="spinner-border spinner-border-sm me-2" /> Carregando...
                </div>
              ) : execucoes.length === 0 ? (
                <p className="text-muted mb-0">Nenhuma execucao registrada.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Inicio</th>
                        <th>Duracao</th>
                        <th>Tamanho</th>
                        <th>Arquivos</th>
                        <th>Disparado por</th>
                        <th>Detalhe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {execucoes.map((e) => (
                        <React.Fragment key={e.id}>
                          <tr>
                            <td><span className={`badge ${STATUS_BADGE[e.status] || 'bg-secondary'}`}>{e.status}</span></td>
                            <td><small>{formatarData(e.iniciado_em)}</small></td>
                            <td><small>{formatarDuracao(e.duracao_ms)}</small></td>
                            <td><small>{formatarBytes(e.bytes)}</small></td>
                            <td><small>{e.arquivos_count ?? '—'}</small></td>
                            <td><small>{e.disparado_por || '—'}</small></td>
                            <td>
                              {e.status === 'erro' || e.status === 'parcial' ? (
                                <small className="text-danger" title={e.erro_mensagem}>
                                  {(e.erro_mensagem || '').slice(0, 60)}
                                </small>
                              ) : (
                                <small className="text-muted" title={e.destino_referencia}>
                                  {(e.nome_arquivo || '').slice(0, 60)}
                                </small>
                              )}
                            </td>
                          </tr>
                          {(e.destinos || []).length > 0 && (
                            <tr>
                              <td colSpan={7} style={{ background: 'rgba(128, 128, 128, 0.08)' }}>
                                <div className="d-flex flex-wrap gap-2 ps-3">
                                  {e.destinos.map((d) => (
                                    <span key={d.id} className={`badge ${d.status === 'sucesso' ? 'bg-success' : 'bg-danger'}`}
                                      title={d.erro_mensagem || d.destino_referencia || ''}>
                                      <i className={`bi ${iconeDestino(d.tipo)} me-1`} />
                                      {d.rotulo || d.tipo}: {d.status}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setHistJob(null)}>Fechar</button>
              <button className="btn btn-outline-primary" onClick={() => abrirHistorico(histJob)}>
                <i className="bi bi-arrow-clockwise me-1" /> Atualizar
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}

function ModalOverlay({ onClose, children }) {
  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
